import tailwindColors from "tailwindcss/colors";

export const BRAND_COLOR = "#e64228";

const ngBlue = {
  50: "#FDFDFE",
  100: "#F8F9FE",
  200: "#EFF2FB",
  300: "#E3E9FC",
  400: "#D5DEFA",
  500: "#C5D1F7",
  600: "#B0BEEC",
  700: "#94A5DF",
  800: "#4D67D5",
  900: "#384FB2",
  950: "#252E54",
};

export const colors = {
  gray: tailwindColors.gray,
  black: tailwindColors.black,
  white: tailwindColors.white,

  // From our Figma component library: https://www.figma.com/design/7o0DkWRg8CN982IMzIrb1Y/1L---Component-Library?node-id=55-4&m=dev
  neutral: {
    50: "#FAF9F8",
    100: "#F6F4F4",
    150: "#F2EFEE",
    200: "#E9E4E2",
    250: "#DFDAD8",
    300: "#D6CFCD",
    350: "#CDC8C6",
    400: "#C0BBBA",
    450: "#AFACAB",
    500: "#878382",
    550: "#726F6F",
    600: "#535150",
    650: "#494746",
    700: "#393837",
    750: "#2E2E2D",
    800: "#232222",
    850: "#1F1E1E",
    900: "#181717",
    950: "#111010",
  },
  accent: ngBlue,
  // We include ng-blue as an available color since although right now the platform's accent color is blue,
  // it could change in the future, and if it does some areas would still need to use blue.
  "ng-blue": ngBlue,
  "ng-green": {
    50: "#FBFEFD",
    100: "#F4FBF9",
    200: "#E6F7F2",
    300: "#D6F1E9",
    400: "#C3E9DE",
    500: "#ACDECF",
    600: "#8BCEBA",
    700: "#56BA9C",
    800: "#29A37E",
    900: "#208369",
    950: "#1D3B32",
  },
  "ng-yellow": {
    50: "#FEFDFB",
    100: "#FFF9ED",
    200: "#FFF2D6",
    300: "#FFE8B5",
    400: "#FFDF9A",
    500: "#FED783",
    600: "#F7CD71",
    700: "#F3BF4E",
    800: "#F7AF15",
    900: "#C48808",
    950: "#58451D",
  },
  "ng-orange": {
    50: "#FEFCFB",
    100: "#FFF7ED",
    200: "#FFEFD6",
    300: "#FFDFB5",
    400: "#FFD19A",
    500: "#FFC182",
    600: "#F5AE73",
    700: "#EC9455",
    800: "#F76415",
    900: "#C45308",
    950: "#582D1D",
  },
  "ng-red": {
    50: "#FFFCFB",
    100: "#FFF8F7",
    200: "#FFEBE6",
    300: "#FFDBD3",
    400: "#FFCCC1",
    500: "#FFBBAE",
    600: "#F8A797",
    700: "#F08C79",
    800: "#E84128",
    900: "#AA230E",
    950: "#5F241A",
  },
  "ng-plum": {
    50: "#FBF8FD",
    100: "#F7F1FB",
    200: "#F2E7F9",
    300: "#F0DFFC",
    400: "#EBD5FA",
    500: "#E3C5F7",
    600: "#D4B0EC",
    700: "#C194DF",
    800: "#9954C7",
    900: "#853AB8",
    950: "#573170",
  },

  brand: {
    50: "#fff3f1",
    100: "#ffe5e1",
    200: "#ffcfc7",
    300: "#ffaea1",
    400: "#fe7f6b",
    500: "#f7563c",
    600: BRAND_COLOR,
    700: "#c12c14",
    800: "#9f2815",
    900: "#842718",
    950: "#481007",
  },
  blue: {
    "50": "#f0f8fe",
    "100": "#deeefb",
    "200": "#c4e2f9",
    "300": "#9bd0f5",
    "400": "#74baef",
    "500": "#4998e8",
    "600": "#347ddc",
    "700": "#2b68ca",
    "800": "#2955a4",
    "900": "#264982",
    "950": "#1c2e4f",
  },
  green: {
    "50": "#effaf1",
    "100": "#d9f2dd",
    "200": "#b5e5bf",
    "300": "#6cc784",
    "400": "#52b56f",
    "500": "#309952",
    "600": "#207b40",
    "700": "#1a6236",
    "800": "#174e2c",
    "900": "#134125",
    "950": "#0a2415",
  },
  yellow: {
    "50": "#fdfce9",
    "100": "#faf8c7",
    "200": "#f7ef92",
    "300": "#f2df52",
    "400": "#eccc23",
    "500": "#dcb416",
    "600": "#be8d10",
    "700": "#986610",
    "800": "#7e5115",
    "900": "#6b4218",
    "950": "#3e220a",
  },
  red: {
    "50": "#fdf3f3",
    "100": "#fce5e4",
    "200": "#fad0ce",
    "300": "#f5afac",
    "400": "#ed8480",
    "500": "#e05853",
    "600": "#cd3b35",
    "700": "#ac2e29",
    "800": "#8e2a26",
    "900": "#772825",
    "950": "#40110f",
  },
  orange: {
    "50": "#fff5ed",
    "100": "#fee9d6",
    "200": "#fcd0ac",
    "300": "#faae77",
    "400": "#f77f3b",
    "500": "#f4611b",
    "600": "#e64610",
    "700": "#be3410",
    "800": "#972915",
    "900": "#7a2514",
    "950": "#421008",
  },
  purple: {
    "50": "#f4f4fe",
    "100": "#eceafd",
    "200": "#dad8fc",
    "300": "#beb9f9",
    "400": "#9e91f4",
    "500": "#7d64ee",
    "600": "#7550e6",
    "700": "#5c31d0",
    "800": "#4d28af",
    "900": "#40238f",
    "950": "#261461",
  },
  // navy is the color used on the legacy side navbar
  navy: "#262E47",
} as const;
